import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MessageBubble,
  type MessageAccountChip,
  type ResendOption,
} from '@/features/chat/MessageBubble';
import { useMessages } from '@/features/chat/useMessages';
import { useReadReceiptTrigger } from '@/features/chat/useReadReceiptTrigger';
import { useThreadMessages } from '@/features/chat/useThreadMessages';

import type { Message } from '@/features/chat/chat.api';

interface MessageListProps {
  /** Classic mode: one conversation's history (`useMessages`). Exactly one
   *  of `conversationId` / `threadKey` must be set. */
  conversationId?: string;
  /** Merged-thread mode: the union window for one phone-thread
   *  (`useThreadMessages`), rendered with the identical virtualization /
   *  scroll-anchoring machinery. */
  threadKey?: string;
  /** Bubbles up a "reply" context-menu pick to the panel, which stores it
   *  and hands it to the `Composer` as `replyingTo`. */
  onReply: (message: Message) => void;
  /** Thread mode only: resolves the per-bubble account chip (color dot +
   *  account label) for a message. `undefined` (classic mode) renders no
   *  chips. Returning `null` for a message also renders no chip (account
   *  unknown — e.g. optimistic entry without `account_id`). */
  accountChipFor?: (message: Message) => MessageAccountChip | null;
  /** Thread mode only: resolves the alternative resend targets (other
   *  CONNECTED member conversations of the same customer) for a REAL failed
   *  outbound row — same plumbing shape as `accountChipFor`. `undefined`
   *  (classic mode) / `null` / empty renders the bubble's own Retry alone. */
  resendOptionsFor?: (message: Message) => ResendOption[] | null;
}

/** Estimated bubble height for the virtualizer's initial layout. */
const ESTIMATED_ROW_HEIGHT = 64;

/** Px threshold from the top that triggers `fetchNextPage`. */
const FETCH_OLDER_THRESHOLD = 80;

/** Px distance from the bottom within which the viewport counts as "at the
 *  bottom" and keeps following new content (see stick-to-bottom below). */
const STICK_TO_BOTTOM_PX = 120;

/**
 * Stable per-row identity. The virtualizer key AND the `displayed` de-dup are
 * BOTH derived from this one function, so they can never disagree — a
 * disagreement would let a duplicate key slip past the de-dup into the
 * virtualizer.
 *
 * Prefer the server `id` (a GUARANTEED-unique primary key). Two DISTINCT
 * persisted messages can carry the SAME `client_ref`: it is an opaque console
 * correlation id with no uniqueness constraint, and a send plus its retry — or
 * a double-submit — reuse one `client_ref` (see `MessageBubble`'s retry, which
 * resends with the failed row's `client_ref`). A merged same-phone thread
 * unions two accounts into one window, so both such rows render together;
 * keying by `client_ref` first collapsed them into ONE React/virtualizer key →
 * duplicate key → whole-app ErrorBoundary crash, for every agent. Only an
 * optimistic, not-yet-persisted row (no `id`) falls back to `client_ref`, which
 * keeps its bubble identity stable across the pending → sent swap.
 */
function messageKey(message: Message | undefined, index: number): string {
  return (message?.id ?? message?.client_ref ?? `idx-${index}`) as string;
}

/**
 * Virtualized message history with reverse-scroll mechanics.
 *
 * BACKEND ORDER: `created_at DESC` — pages[0].items[0] is the newest.
 * UI ORDER: oldest-at-top, newest-at-bottom (standard chat).
 *
 * REVERSE-SCROLL APPROACH: array-reverse + a normal top-down virtualizer.
 * Considered alternatives:
 *
 *   - CSS `flex-direction: column-reverse`: simple but accessibility-hostile
 *     (Tab order, screen-reader reading order is wrong) and confuses
 *     `react-virtual`'s scroll math.
 *   - Reverse the virtualizer's `index` map: viable but adds a layer of
 *     index-translation between the data and the layout — every scroll-to,
 *     measure-element, and overscan calculation has to invert.
 *
 * Flatten + reverse is the cleanest: `displayed[0]` is the oldest message,
 * `displayed[displayed.length - 1]` is the newest. We then anchor the
 * viewport at the bottom on mount and preserve the scroll position when
 * older messages prepend (`fetchNextPage` resolves).
 */
export function MessageList({
  conversationId,
  threadKey,
  onReply,
  accountChipFor,
  resendOptionsFor,
}: MessageListProps): React.JSX.Element {
  // Both hooks are called unconditionally (Rules of Hooks); each is
  // `enabled`-gated by its null id, so exactly one issues a fetch. The
  // active one drives the list.
  const conversationQuery = useMessages(conversationId ?? null);
  const threadQuery = useThreadMessages(threadKey ?? null);
  const query = threadKey != null ? threadQuery : conversationQuery;

  // Flatten pages → reverse → array indexed oldest-first.
  const displayed: Message[] = useMemo(() => {
    if (!query.data) return [];
    const flat = query.data.pages.flatMap((page) => page.items);
    // Reverse off the flat array so the per-page item order (DESC) becomes
    // oldest-first. `slice()` is implicit in spread; toReversed() lands in
    // ES2023 but isn't typed everywhere yet — `[...flat].reverse()` is
    // friendlier for the project's TS settings.
    const reversed = [...flat].reverse();
    // Defensive de-dup by the SAME identity the virtualizer keys on
    // (`messageKey`). The merge helpers (`mergeServerMessage` / `patchCacheWith`)
    // normally keep the cache dup-free, but a 30s poll refetch racing the
    // optimistic → server swap, or a genuine backend duplicate, could
    // momentarily surface one identity twice — and a SINGLE duplicate React key
    // takes the whole app down through the top-level ErrorBoundary. Keeping the
    // first occurrence makes a duplicate virtualizer key impossible by
    // construction (belt to `messageKey`'s braces). A dup is normally absent, so
    // this is an O(n) pass that allocates nothing extra in the common case.
    const seen = new Set<string>();
    const deduped: Message[] = [];
    for (let i = 0; i < reversed.length; i += 1) {
      const key = messageKey(reversed[i], i);
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(reversed[i]);
    }
    return deduped;
  }, [query.data]);

  // Resolver for the quoted-preview block: `reply_to_platform_message_id`
  // on a message is another message's `platform_message_id` (its wamid).
  // Built once per `displayed` change rather than searched per-bubble —
  // O(n) instead of O(n²) over the loaded page window. Only messages that
  // actually have a `platform_message_id` (i.e. most inbound + confirmed
  // outbound rows; optimistic sends don't yet) are keyed.
  const byPlatformMessageId = useMemo(() => {
    const map = new Map<string, Message>();
    for (const m of displayed) {
      if (m.platform_message_id) map.set(m.platform_message_id, m);
    }
    return map;
  }, [displayed]);

  const parentRef = useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: displayed.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 6,
    measureElement: (node) => node.getBoundingClientRect().height,
    // A stable key ensures the virtualizer's measurement cache survives a
    // patch in place. `messageKey` prefers the unique server id (see its doc)
    // — the same identity `displayed` was de-duplicated by, so the virtualizer
    // is guaranteed a duplicate-free key set.
    getItemKey: (index) => messageKey(displayed[index], index),
  });

  // --- Scroll anchoring ----------------------------------------------------
  //
  // Two mechanisms:
  //
  //   (A) STICK-TO-BOTTOM. `stickToBottomRef` tracks whether the operator is
  //       "at the bottom" (within STICK_TO_BOTTOM_PX, sampled on every scroll
  //       event). While true, every commit in which the CONTENT or the LAYOUT
  //       actually moved (`displayed` identity or `el.scrollHeight` changed)
  //       re-pins the viewport to the true bottom (`el.scrollHeight`).
  //       Pinning on layout changes — not only on data changes — is what
  //       makes the pin self-correcting: the virtualizer lays rows out at
  //       ESTIMATED_ROW_HEIGHT and measures them lazily, so any one-shot
  //       scroll lands short; each measurement pass re-renders with a new
  //       total size, the effect re-runs, and the viewport converges on the
  //       real bottom. It also makes following new messages burst-proof: the
  //       decision is the flag captured BEFORE the append, not a
  //       distance-from-bottom computed after N fresh rows already inflated
  //       `scrollHeight` (the old check silently stopped following for any
  //       batch of ≥2 messages). Two guards keep the pin from fighting the
  //       operator (detailed at the effect): a CHANGE GATE (pin only when
  //       content/layout actually moved) and a SCROLL-DIRECTION GATE (never
  //       pin while the operator is scrolling UP — the virtualizer
  //       flushSync-commits mid-gesture and upward scrolling grows the
  //       measured height, which the change gate alone cannot tell apart from
  //       new content). Scrolling up past the threshold releases the pin — an
  //       operator reading history is never yanked down — and returning to
  //       the bottom re-arms it. A conversation/thread switch resets the flag
  //       to true: opening a conversation ALWAYS lands on the newest message.
  //       Known limitation (minor, self-correcting): the flag is sampled only
  //       from scroll events, so a container resize / browser zoom that leaves
  //       the operator visually at the bottom without a scroll event keeps a
  //       previously-released flag false until their next scroll — new
  //       messages don't auto-follow until they nudge the scrollbar once. A
  //       data-driven self-heal was tried and removed: it re-armed on
  //       height-neutral cache patches (delivery-status / reaction) and, in a
  //       barely-overflowing window, yanked an operator out of deliberately
  //       pulled history.
  //
  //   (B) FETCH-OLDER RESTORE. On a `fetchNextPage` resolution (older
  //       messages prepended) keep the visible row stable by snapshotting the
  //       scroll geometry before the data flips and re-applying it after.
  //       Exclusivity with (A) is enforced explicitly: triggering a fetch
  //       releases the sticky flag (reaching the top to pull older history IS
  //       history-reading intent — decisive even in a barely-overflowing
  //       window whose whole scroll range sits inside the sticky band), and a
  //       snapshot that resolves while the flag is armed again (the operator
  //       returned to the bottom mid-flight) is discarded instead of
  //       teleporting them back into history.

  const prevCountRef = useRef(0);
  const prevFirstIdRef = useRef<string | null>(null);
  const pendingScrollRestoreRef = useRef<{ height: number; top: number } | null>(null);
  const stickToBottomRef = useRef(true);
  // One-shot guard: the fetch-older restore assigns `scrollTop`
  // programmatically, and the resulting scroll event must NOT re-arm
  // stickiness — in a barely-overflowing window the restored position still
  // sits inside the sticky band, and re-arming would let the very next
  // measurement pass yank the operator back to the bottom.
  const suppressNextStickSampleRef = useRef(false);
  // Handle for the fetch-older restore's rAF so a conversation switch (which
  // reuses the SAME scroll node) can cancel an in-flight restore — otherwise
  // a stale rAF closes over the old snapshot and writes a foreign scrollTop
  // into the freshly-opened conversation.
  const restoreRafRef = useRef<number | null>(null);
  // Change detectors for the pin effect (A): pin only when the content or the
  // layout actually moved, never on scroll-induced or unrelated commits.
  const lastSeenDisplayedRef = useRef<Message[] | null>(null);
  const lastSeenHeightRef = useRef(-1);
  // While true, pins bypass the scroll-direction gate. Armed by the switch
  // reset because the virtualizer instance — and its `scrollDirection` —
  // survives an in-place conversation switch: the operator may have left the
  // previous conversation mid-upward-scroll, so a stale `'backward'` (its
  // 150ms reset hasn't fired) would otherwise suppress the land-on-newest
  // pin AND the follow-up convergence pins. Consumed by the first real
  // scroll event in the new conversation (handleScroll): any scroll event —
  // including the pin's own echo — refreshes `scrollDirection` for THIS
  // conversation, ending the stale window.
  const forcePinRef = useRef(true);

  // The component is NOT remounted on a conversation/thread switch (ChatPanel
  // renders it in place), so the anchoring state machine must be reset by
  // hand when the data source changes — sticky again (every open lands at the
  // bottom), no stale prepend bookkeeping carried across conversations.
  useLayoutEffect(() => {
    stickToBottomRef.current = true;
    suppressNextStickSampleRef.current = false;
    prevCountRef.current = 0;
    prevFirstIdRef.current = null;
    pendingScrollRestoreRef.current = null;
    lastSeenDisplayedRef.current = null;
    lastSeenHeightRef.current = -1;
    forcePinRef.current = true;
    if (restoreRafRef.current !== null) {
      cancelAnimationFrame(restoreRafRef.current);
      restoreRafRef.current = null;
    }
  }, [conversationId, threadKey]);

  // Just *before* fetchNextPage resolves and the virtualizer re-lays out
  // we snapshot the scroll geometry; we re-apply after. We trigger the
  // snapshot from the scroll handler so it's always taken with the
  // freshest values.
  useEffect(() => {
    if (!query.isFetchingNextPage) return;
    const el = parentRef.current;
    if (!el) return;
    pendingScrollRestoreRef.current = {
      height: el.scrollHeight,
      top: el.scrollTop,
    };
  }, [query.isFetchingNextPage]);

  // (B) Fetch-older restore: keep the visible message stable when a page of
  // older messages prepends. The delta between the new and the snapshotted
  // scrollHeight is exactly the height of the prepended block; offsetting
  // scrollTop by that delta keeps the operator's visual row where it was.
  useLayoutEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    const count = displayed.length;
    const firstId = count > 0 ? displayed[0].id : null;
    const olderPrepended =
      prevCountRef.current > 0 &&
      count > prevCountRef.current &&
      prevFirstIdRef.current !== firstId &&
      pendingScrollRestoreRef.current !== null;

    if (olderPrepended) {
      const snapshot = pendingScrollRestoreRef.current;
      pendingScrollRestoreRef.current = null;
      // A snapshot resolving while stickiness re-armed is STALE — the
      // operator scrolled back to the bottom while the fetch was in flight,
      // and restoring would teleport them into mid-history. Drop it; the
      // sticky pin (A) keeps them where they now are.
      if (snapshot && !stickToBottomRef.current) {
        // requestAnimationFrame ensures the new layout has flushed; the
        // virtualizer's per-row measurement happens lazily so we ratchet on
        // the next frame. The handle lets a conversation switch cancel a
        // restore that hasn't fired yet.
        if (restoreRafRef.current !== null) cancelAnimationFrame(restoreRafRef.current);
        restoreRafRef.current = requestAnimationFrame(() => {
          restoreRafRef.current = null;
          const newHeight = el.scrollHeight;
          const delta = newHeight - snapshot.height;
          if (delta > 0) {
            // The assignment fires a scroll event; keep it from re-arming
            // stickiness (see suppressNextStickSampleRef).
            suppressNextStickSampleRef.current = true;
            el.scrollTop = snapshot.top + delta;
          }
        });
      }
    }

    prevCountRef.current = count;
    prevFirstIdRef.current = firstId;
  }, [displayed]);

  // (A) Stick-to-bottom pin. Deliberately NO dependency array — measurement
  // passes re-render without changing `displayed` and must still re-pin, or
  // the viewport is left stranded mid-history as the layout grows (the
  // "opens above the newest message" bug). Two guards make it safe against
  // the ways `react-virtual` commits:
  //
  //   1. CHANGE GATE — pin only when the content (`displayed` identity) or the
  //      layout (`scrollHeight`) actually moved. Commits with neither (an
  //      `isScrolling` flip, an unrelated parent re-render such as a reply
  //      click or a lock-cache tick) never scroll.
  //   2. SCROLL-DIRECTION GATE — never pin while the operator is actively
  //      scrolling UP. The virtualizer flushSync-commits DURING a scroll
  //      gesture, and scrolling up into not-yet-measured rows GROWS
  //      `scrollHeight` (lazy `measureElement`), so the change gate alone
  //      would let those mid-gesture commits re-pin and rubber-band the
  //      operator back to the bottom — they could never leave the sticky
  //      band. `scrollDirection === 'backward'` is set by the virtualizer's
  //      own scroll handler in the SAME flushSync that drives this effect, so
  //      it is reliable regardless of listener order; it is only ever
  //      `'backward'` for a user-initiated upward scroll (the pin's own
  //      scrollTo moves DOWN → `'forward'`; measurement without scrolling →
  //      `null`). A change gated mid-gesture is NOT consumed, so the
  //      direction-reset commit (~150ms after the last scroll event)
  //      delivers the owed pin — burst-follow and open-time convergence are
  //      delayed by at most that reset, never lost.
  //
  // When already at the bottom the pin is a no-op, so convergence settles
  // instead of looping.
  useLayoutEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const height = el.scrollHeight;
    const dataChanged = lastSeenDisplayedRef.current !== displayed;
    const heightChanged = lastSeenHeightRef.current !== height;
    if (!dataChanged && !heightChanged) return;
    if (!stickToBottomRef.current) {
      // Released: no pin is owed for this change — consume it.
      lastSeenDisplayedRef.current = displayed;
      lastSeenHeightRef.current = height;
      return;
    }
    if (rowVirtualizer.scrollDirection === 'backward' && !forcePinRef.current) {
      // Mid-upward-gesture: hold the pin but do NOT consume the change. The
      // virtualizer's isScrolling-reset commit (~150ms after the last scroll
      // event) re-runs this effect with the change still pending and the
      // direction cleared, delivering the owed pin. Consuming here would
      // strand the viewport above a burst that landed during an in-band
      // nudge until the next unrelated change.
      return;
    }
    lastSeenDisplayedRef.current = displayed;
    lastSeenHeightRef.current = height;
    el.scrollTo({ top: el.scrollHeight });
  });

  // Infinite-scroll trigger: when the user scrolls within
  // FETCH_OLDER_THRESHOLD of the top, fetch the next page.
  function handleScroll(): void {
    const el = parentRef.current;
    if (!el) return;
    if (suppressNextStickSampleRef.current) {
      // Echo of the fetch-older restore's programmatic scroll — not operator
      // intent; leave the flag as-is.
      suppressNextStickSampleRef.current = false;
    } else {
      // Sample stickiness on every real scroll: the pin's own echo lands at
      // distance ≈ 0 and keeps the flag armed; an upward scroll past the
      // threshold releases it, and returning to the bottom re-arms it.
      stickToBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX;
      // Any scroll event also refreshes the virtualizer's `scrollDirection`
      // for THIS conversation, so the stale-direction bypass window ends
      // here (see forcePinRef).
      forcePinRef.current = false;
    }
    if (
      el.scrollTop <= FETCH_OLDER_THRESHOLD &&
      query.hasNextPage &&
      !query.isFetchingNextPage
    ) {
      // Take a snapshot before the data changes so the layout-effect can
      // restore. The other useEffect does this too, but firing here
      // closes a race window where `isFetchingNextPage` flips true on
      // the same render the data arrives.
      pendingScrollRestoreRef.current = {
        height: el.scrollHeight,
        top: el.scrollTop,
      };
      // Deliberately reaching the top to pull older history is
      // history-reading intent — release the pin even in a
      // barely-overflowing window whose whole range sits inside the
      // sticky band.
      stickToBottomRef.current = false;
      void query.fetchNextPage();
    }
  }

  // --- WhatsApp read receipts (2026-09 F4, spec 04 §3 / §9) ------------------
  //
  // Observe-only: the trigger READS `displayed` and the stick-to-bottom flag
  // (never writes the scroll state above, holds no React state, renders
  // nothing). "At the newest message" also requires the list itself to be
  // mounted — `parentRef` is null in the loading / error / empty branches,
  // where the still-armed flag would vouch for messages that are not on screen.
  useReadReceiptTrigger({
    messages: displayed,
    isAtBottom: () => parentRef.current !== null && stickToBottomRef.current,
    viewKey: threadKey ?? conversationId ?? null,
  });

  // --- Render branches -----------------------------------------------------

  if (query.isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-4" data-testid="messages-skeletons">
        <div className="flex items-center justify-center py-2">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            className={
              i % 2 === 0
                ? 'h-10 w-2/3 self-start rounded-2xl'
                : 'h-10 w-1/2 self-end rounded-2xl'
            }
          />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4 py-6 text-sm text-muted-foreground">
        <p>Could not load messages. Check your connection and try again.</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => query.refetch()}
        >
          <RefreshCw className="mr-2 size-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  if (displayed.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 text-center text-sm text-muted-foreground">
        No messages yet — start the conversation!
      </div>
    );
  }

  const virtualItems = rowVirtualizer.getVirtualItems();

  return (
    <div
      ref={parentRef}
      onScroll={handleScroll}
      className="min-h-0 flex-1 overflow-y-auto py-3"
      data-testid="message-virtual-scroll"
    >
      {/* Loading-older indicator at the very top of the scrollable area. */}
      {query.isFetchingNextPage && (
        <div className="flex items-center justify-center py-2" aria-live="polite">
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
      )}

      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const message = displayed[virtualRow.index];
          // The virtualizer's overscan can reference an index PAST the loaded
          // window — most reliably when we anchor to the bottom on open, where
          // the trailing overscan runs off the end of `displayed`. `getItemKey`
          // already guards this with `m?.`; the render body must too, or a bare
          // `message.<field>` deref throws and takes the whole app down through
          // the top-level ErrorBoundary. (Regression: a merged same-phone thread
          // reliably reproduced this — see MessageList.overscan.test.tsx.)
          if (!message) return null;
          const quotedMessage = message.reply_to_platform_message_id
            ? byPlatformMessageId.get(message.reply_to_platform_message_id)
            : undefined;
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={(node) => rowVirtualizer.measureElement(node)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <MessageBubble
                message={message}
                quotedMessage={quotedMessage}
                accountChip={accountChipFor ? accountChipFor(message) : null}
                resendOptions={resendOptionsFor ? resendOptionsFor(message) : null}
                threadKey={threadKey ?? null}
                onReply={onReply}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
